import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faVial, faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { ErrorResponse } from '../../custom-fetch';
import { Route } from '../../routes';
import { QueryKey } from '../../services/query-keys';
import { useOrganizationsService } from '../../services/organizations-service';
import IOrganization from '../../shared/IOrganization';
import ErrorsAlert from '../ErrorsAlert/ErrorsAlert';
import Spinner from '../ui/Spinner/Spinner';
import Table from '../ui/Table/Table';
import { useSortBy } from '../../hooks/useSortBy';
import Input, { EInputType } from '../ui/Input/Input';

enum EOrganizationSortBy {
  name = "name",
  agentCode = "agentCode",
  apiCode = "apiCode",
  apiKey = "apiKey",
  forcedCdp = "forcedCdp",
  sendConfirmation = "sendConfirmation",
  consumerRate = "consumerRate"
}

const OrganizationsList: React.FC = () => {
  const { fetchOrganizations, testCredentials } = useOrganizationsService();

  const navigate = useNavigate();
  const [filterText, setFilterText] = useState<string>('');
  const [revealedApiKeys, setRevealedApiKeys] = useState<Set<string>>(new Set());
  const [testStatuses, setTestStatuses] = useState<Record<string, 'testing' | 'success' | 'error'>>({});

  const toggleApiKey = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRevealedApiKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleTestCredentials = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setTestStatuses(prev => ({ ...prev, [id]: 'testing' }));
    try {
      await testCredentials(id);
      setTestStatuses(prev => ({ ...prev, [id]: 'success' }));
    } catch (err: any) {
      setTestStatuses(prev => ({ ...prev, [id]: 'error' }));
    }
  };

  const {
    data: organizations,
    isPending,
    isError,
    error,
  } = useQuery<IOrganization[], ErrorResponse>({
    queryKey: [QueryKey.organizations],
    queryFn: fetchOrganizations,
    staleTime: 5000
  });

  const sortFunction = (org: IOrganization, field: string) => {
    switch (field) {
      case EOrganizationSortBy.name:
        return (org.name || "").toLowerCase();
      case EOrganizationSortBy.agentCode:
        return (org.apiAgent || "").toLowerCase();
      case EOrganizationSortBy.apiCode:
        return (org.apiCode || "").toLowerCase();
      case EOrganizationSortBy.apiKey:
        return (org.apiKey || "").toLowerCase();
      case EOrganizationSortBy.forcedCdp:
        return org.forcedCdp ? 1 : 0;
      case EOrganizationSortBy.sendConfirmation:
        return org.sendConfirmation ? 1 : 0;
      case EOrganizationSortBy.consumerRate:
        return org.consumerRate ? 1 : 0;
      default:
        return (org as any)[field]?.toString()?.toLowerCase() || "";
    }
  };

  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];
    if (!filterText) return organizations;
    const lowerFilter = filterText.toLowerCase();
    return organizations.filter(org => 
      (org.name || "").toLowerCase().includes(lowerFilter) ||
      (org.apiAgent || "").toLowerCase().includes(lowerFilter) ||
      (org.apiCode || "").toLowerCase().includes(lowerFilter) ||
      (org.apiKey || "").toLowerCase().includes(lowerFilter)
    );
  }, [organizations, filterText]);

  const { sorted, sort, changeSortOrDirection, getChevron } = useSortBy(filteredOrganizations, sortFunction);

  if (isError) {
    return <ErrorsAlert errors={error.messages} />;
  }

  if (isPending) {
    return <Spinner />;
  }

  if (!organizations || organizations.length === 0) {
    return <p>No organizations</p>;
  }

  const openHandler = (id: string) => {
    navigate(Route.organization(id));
  }

  const headers = [
    { label: "Name", sortKey: EOrganizationSortBy.name },
    { label: "Agent Code", sortKey: EOrganizationSortBy.agentCode },
    { label: "API Code", sortKey: EOrganizationSortBy.apiCode },
    { label: "API Key", sortKey: EOrganizationSortBy.apiKey },
    { label: "Test API", sortKey: "testApi" },
    { label: "Forced CDP", sortKey: EOrganizationSortBy.forcedCdp },
    { label: "Send Confirmation", sortKey: EOrganizationSortBy.sendConfirmation },
    { label: "Rate Type", sortKey: EOrganizationSortBy.consumerRate },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <div className="w-64">
          <Input 
            type={EInputType.text} 
            value={filterText} 
            onChange={(val) => setFilterText(val as string)} 
            inputName="filterOrganizations" 
            placeholder="filter agents..." 
          />
        </div>
      </div>
      <Table hover>
        <thead>
        <tr>
          {headers.map(header => (
            <th 
              key={header.label}
              onClick={() => changeSortOrDirection(header.sortKey)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {header.label}
                {sort === header.sortKey && getChevron()}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((org) => (
          <tr key={org.id} onClick={() => openHandler(org.id)}>
            <td>{org.name}</td>
            <td>{org.apiAgent}</td>
            <td>{org.apiCode}</td>
            <td>
              {!org.apiKey ? (
                '-'
              ) : revealedApiKeys.has(org.id) ? (
                org.apiKey
              ) : (
                <FontAwesomeIcon 
                  icon={faSearch} 
                  onClick={(e) => toggleApiKey(org.id, e)} 
                  style={{ cursor: 'pointer' }} 
                  title="Reveal API Key" 
                  className="text-secondary"
                />
              )}
            </td>
            <td>
              {testStatuses[org.id] === 'testing' ? (
                 <FontAwesomeIcon icon={faSpinner} spin className="text-secondary" />
              ) : testStatuses[org.id] === 'success' ? (
                 <FontAwesomeIcon icon={faCheck} className="text-success" />
              ) : testStatuses[org.id] === 'error' ? (
                 <FontAwesomeIcon icon={faTimes} className="text-danger" />
              ) : (
                <FontAwesomeIcon 
                  icon={faVial} 
                  onClick={(e) => handleTestCredentials(org.id, e)} 
                  style={{ cursor: 'pointer' }} 
                  title="Test API Agent Credentials" 
                  className="text-primary"
                />
              )}
            </td>
            <td>{org.forcedCdp ? (org.cdp || 'Yes') : '-'}</td>
            <td>{org.sendConfirmation ? (org.confirmationEmail || 'Yes') : '-'}</td>
            <td>{org.consumerRate ? 'Consumer' : 'Net Rate'}</td>
          </tr>
        ))}
      </tbody>
      </Table>
    </div>
  );
};

export default OrganizationsList;
